import { supabase } from "./supabaseClient";

export type OwnershipMode = 'USER' | 'TINANEY';
export type Sensitivity = 'LOW' | 'MEDIUM' | 'HIGH';
export type IdentityLevel = 'SIGNED_DEVICE' | 'GATEWAY';

export interface Device {
  id: string;
  owner_id: string;
  label: string | null;
  device_type: string;
  ownership_mode: OwnershipMode;
  sensitivity: Sensitivity;
  identity_level: IdentityLevel;
  status: 'ACTIVE' | 'DISABLED';
  geo_lat: number | null;
  geo_lng: number | null;
  last_seen: string | null;
  created_at: string;
}

export interface Rollup {
  metric: string; day: string; count: number;
  avg: number | null; min: number | null; max: number | null; last: number | null;
}

export interface DataProduct {
  id: string; owner_id: string; device_id: string; title: string; description: string | null;
  metric: string | null; period_start: string | null; period_end: string | null;
  price_credits: number; access: 'FREE' | 'PAID'; sensitivity: Sensitivity; active: boolean; created_at: string;
}

// ── Devices ──────────────────────────────────────────────────────────────────
export async function listMyDevices(): Promise<Device[]> {
  const { data, error } = await supabase.from('devices').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as Device[];
}

export interface ProvisionInput {
  label: string; device_type: string; ownership_mode: OwnershipMode;
  sensitivity: Sensitivity; identity_level: IdentityLevel; geo_lat?: number | null; geo_lng?: number | null;
}
// Returns the one-time device key (shown once) + the new device id.
export async function provisionDevice(p: ProvisionInput): Promise<{ device_id: string; device_key: string }> {
  const { data, error } = await supabase.rpc('provision_device', {
    p_label: p.label, p_device_type: p.device_type, p_ownership_mode: p.ownership_mode,
    p_sensitivity: p.sensitivity, p_identity_level: p.identity_level,
    p_geo_lat: p.geo_lat ?? null, p_geo_lng: p.geo_lng ?? null,
  });
  if (error) throw error;
  return { device_id: data.device_id, device_key: data.device_key };
}

export async function rotateDeviceKey(deviceId: string): Promise<string> {
  const { data, error } = await supabase.rpc('rotate_device_key', { p_device_id: deviceId });
  if (error) throw error;
  return data.device_key;
}

export async function getDeviceRollups(deviceId: string, days = 30, metric?: string): Promise<Rollup[]> {
  const { data, error } = await supabase.rpc('get_device_rollups', { p_device_id: deviceId, p_days: days, p_metric: metric ?? null });
  if (error) throw error;
  return (data || []) as Rollup[];
}

// ── Data products / marketplace ──────────────────────────────────────────────
export async function createDataProduct(input: {
  device_id: string; title: string; description?: string; metric?: string;
  period_start?: string | null; period_end?: string | null; price_credits: number; access: 'FREE' | 'PAID';
}): Promise<string> {
  const { data, error } = await supabase.rpc('create_data_product', {
    p_device_id: input.device_id, p_title: input.title, p_description: input.description ?? null,
    p_metric: input.metric ?? null, p_period_start: input.period_start ?? null, p_period_end: input.period_end ?? null,
    p_price_credits: input.price_credits, p_access: input.access,
  });
  if (error) throw error;
  return data as string;
}

export async function listMarketProducts(): Promise<DataProduct[]> {
  const { data, error } = await supabase.from('data_products').select('*').eq('active', true).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as DataProduct[];
}

export async function listMyProducts(): Promise<DataProduct[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase.from('data_products').select('*').eq('owner_id', user.id).order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as DataProduct[];
}

export async function listMyPurchasedProductIds(): Promise<Set<string>> {
  const { data, error } = await supabase.from('data_purchases').select('product_id');
  if (error) throw error;
  return new Set((data || []).map((r: any) => r.product_id));
}

export async function purchaseDataProduct(productId: string): Promise<any> {
  const { data, error } = await supabase.rpc('purchase_data_product', { p_product_id: productId });
  if (error) throw error;
  return data;
}

export async function getDataProductAccess(productId: string): Promise<any> {
  const { data, error } = await supabase.rpc('data_product_access', { p_product_id: productId });
  if (error) throw error;
  return data;
}

const IOT_URL = (import.meta as any).env.VITE_IOT_WORKER_URL || '';

export interface ScopedExport {
  raw_allowed: boolean;
  file_count: number;
  files: { key: string; url: string }[];
  sensitivity: string;
  rollups: any[];
  configured: boolean;
}

// Scoped bulk manifest from the ingestion Worker (raw files filtered to the buyer's
// purchased device + time range). Returns configured:false if the Worker URL isn't set.
export async function getScopedExport(productId: string): Promise<ScopedExport> {
  if (!IOT_URL) return { raw_allowed: false, file_count: 0, files: [], sensitivity: 'LOW', rollups: [], configured: false };
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(`${IOT_URL}/export?product=${productId}`, { headers: { Authorization: `Bearer ${session?.access_token}` } });
  if (!r.ok) throw new Error('Export request failed');
  return { ...(await r.json()), configured: true };
}

// Download one scoped raw object (auth header → blob → save).
export async function downloadScopedFile(file: { key: string; url: string }): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(file.url, { headers: { Authorization: `Bearer ${session?.access_token}` } });
  if (!r.ok) throw new Error('Download failed');
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u; a.download = file.key.split('/').pop() || 'data.json';
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u);
}
